'use client';

import { useState } from 'react';

import { Button } from '@/components/atoms';
import { ModalWrapper } from '@/components/organisms';
import { ActionButtons } from '@/components/molecules';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum } from '@/domain/constants/constants';
import { toast } from 'react-toastify';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useScoreCard } from '@/contexts/ScoreCardProvider';

interface ConfirmCancelMatchModalProps {
	title: string;
	matchId: string;
	setIsSavedHomePlayers?: (isSaved: boolean) => void;
	setIsSavedAwayPlayers?: (isSaved: boolean) => void;
	disabled?: boolean;
}

export const ConfirmCancelMatchModal = ({ title, matchId, setIsSavedHomePlayers, setIsSavedAwayPlayers, disabled }: ConfirmCancelMatchModalProps) => {
	const { matchupData, updateMatchLineupStatus } = useScoreCard();

	const queryClient = useQueryClient();
	const { mutate: updateMatchMutation } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success('Match canceled successfully');
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupData.uuid) });
			if (setIsSavedHomePlayers) {
				setIsSavedHomePlayers(true);
			}
			if (setIsSavedAwayPlayers) {
				setIsSavedAwayPlayers(true);
			}
			updateMatchLineupStatus(matchId, true);
		}
	});

	const [showModal, setShowModal] = useState<boolean>(false);
	const formatedTitle = title === 'No title for this match' ? '' : title;

	const toggleModal = () => {
		setShowModal((prev) => !prev);
	};

	const handSubmit = () => {
		updateMatchMutation({
			matchId: matchId,
			updateData: {
				matchStatus: MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS],
				matchCompletedType: MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE
			}
		});
		toggleModal();
	};

	const handleCancel = () => {
		toggleModal();
	};

	return (
		<>
			{!disabled && (
				<Button
					className="h-8 flex w-full items-center justify-center text-xs font-semibold text-gray-900 transition-all duration-200 sm:w-auto hover:bg-white hover:shadow-sm border border-violet-25/70 hover:border-gray-400"
					onClick={toggleModal}
					disabled={disabled}
				>
					Cancel match
				</Button>
			)}

			{showModal && (
				<ModalWrapper
					overlayClassName="z-10"
					className="max-w-[500px]"
					title="Confirm cancel match"
					onClose={toggleModal}
					closeOnOutsideClick
				>
					<div className="flex flex-col gap-2">
						<p className="text-sm font-medium py-6">
							You are about to Cancel the {formatedTitle} match. This will remove the match from the Game Day and will not affect the
							teams statistics.
						</p>
						<ActionButtons
							className="flex justify-end gap-3"
							closeButtonClassName="w-32 text-gray-700"
							submitButtonClassName="w-32 bg-orange-500 text-white hover:bg-orange-700"
							onClose={handleCancel}
							onSubmit={handSubmit}
							submitButtonText="Confirm"
							closeButtonText="Cancel"
							submitButtonDisabled={false}
						/>
					</div>
				</ModalWrapper>
			)}
		</>
	);
};
